/* eslint-disable react/prop-types */

import styled from '@emotion/styled';
import { Cloudinary } from 'cloudinary-core';

import { CloudinaryDefaultConfig, getComponentsFromCloudinaryUrl } from '../../../helpers/cloudfront_helper';
import Linking from '../../PageBuilder/helpers/linking';
import { dataTrackAttribute, mapPathFragmentFromDomain } from '../../helpers/analytics';
import { connectLocation } from '../../location/LocationConnectors';
import RichText from '../PageBuilder/RichText';
import Text from '../PageBuilder/utilities/Text';

const HeaderLink = styled.a`
  color: #000000;
  text-decoration: none;

  &:hover {
    color: #000000;
    text-decoration: underline;
  }
`;

const targetProp = url => {
  const { opensInBlank } = Linking.Parse(url);
  return opensInBlank ? '_blank' : undefined;
};

const Link = connectLocation(({ t, item, mobile, locale: { code } }) =>
  mobile ? (
    <a
      href={item.url}
      className="nav-link"
      data-track="nav-click"
      data-track-nav-select={dataTrackAttribute(item.url, code, true, mapPathFragmentFromDomain())}
      target={targetProp(item.url)}
    >
      <Text content={item.title} />
    </a>
  ) : (
    <a
      className="nav-link"
      aria-label={item.title}
      data-track="nav-click"
      data-track-nav-select={dataTrackAttribute(item.url, code, true, mapPathFragmentFromDomain())}
      href={item.url}
      target={targetProp(item.url)}
    >
      <Text content={item.title} />
      {item.is_new && <span className="item-state">{t('meganav.new')}</span>}
    </a>
  )
);

const Subcolumn = ({ item /* , mobile */ }) => (
  <div className="column-title">
    <h4>
      <Text content={item.title} />
    </h4>
  </div>
);

const Header = ({ item /* , mobile */ }) => (
  <h6 className="group-heading">
    {item.url?.trim() ? (
      <HeaderLink href={item.url} target={targetProp(item.url)}>
        <Text content={item.title} />
      </HeaderLink>
    ) : (
      <Text content={item.title} />
    )}
  </h6>
);

const Card = connectLocation(({ t, locale: { code }, item /* , mobile */ }) => {
  const cloudinary = new Cloudinary(CloudinaryDefaultConfig);
  // eslint-disable-next-line camelcase
  const { public_id, type } = getComponentsFromCloudinaryUrl(item.image?.source || item.image?.file);
  const src = cloudinary.url(public_id, {
    crop: 'fit',
    fetchFormat: 'auto',
    quality: 60,
    type,
  });
  return (
    <a
      href={item.url}
      target={targetProp(item.url)}
      className="image-parent"
      data-track="nav-click"
      data-track-nav-select={dataTrackAttribute(item.url, code, true, mapPathFragmentFromDomain())}
    >
      {item.is_new && <span className="item-state-label">{t('meganav.new')}</span>}
      <div className="image-container">
        <img src={src} alt={item.image?.alt} className="image-display" decoding="async" loading="lazy" />
      </div>
      <div className="image-heading">
        <Text content={item.title} />
      </div>
      <div className="image-subheading">
        <Text content={item.description?.toString()} />
      </div>
    </a>
  );
});

const Decal = ({ item, mobile }) =>
  !mobile && (
    <picture>
      <source media="(min-width: 1200px)" srcSet={item.image?.source || item.image?.file} />
      <img
        src="#"
        alt={item.image?.alt}
        width={item.width}
        height={item.height}
        className="decal"
        style={{ right: `${item.right}px`, bottom: `${item.bottom}px` }}
        decoding="async"
        loading="lazy"
      />
    </picture>
  );

const ArticlePreview = connectLocation(({ item /* , mobile */ }) => (
  <div className="article-preview">
    <div className="article-preview-text">
      <RichText content={item.description} />
    </div>
    <a className="article-preview-button nav-link chevron-link" href={item.url} target={targetProp(item.url)}>
      <Text content={item.title} />
    </a>
  </div>
));

const LinkTypes = {
  text: Link,
  header: Header,
  card: Card,
  decal: Decal,
  'article-preview': ArticlePreview,
  subcolumn: Subcolumn,
};

export default ({ items, mobile }) => (
  <>
    {items.map((item, i) => {
      const Component = LinkTypes[item.link_type];
      // eslint-disable-next-line react/no-array-index-key
      return Component && <Component key={i} item={item} mobile={mobile} />;
    })}
  </>
);
